import { ReloadOutlined } from '@ant-design/icons';
import { PageHeader } from '@ant-design/pro-components';
import { useRequest } from 'ahooks';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Form,
  Input,
  Popconfirm,
  Result,
  Row,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import {
  AiAdminConversation,
  AiAdminConversationQuery,
  aiAssistantAdminApi,
} from '../../../api/aiAssistant';
import { useCompany } from '../../../hooks/company';
import MessageBubble from '../../ai-assistant/MessageBubble';
import { foldMessages } from '../../ai-assistant/trace';
import {
  EMPTY_PLACEHOLDER,
  formatCacheHitRate,
  formatDuration,
  formatExactTokens,
  formatTokens,
} from './usage';

const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

interface FilterState {
  keyword?: string;
  userId?: string;
  range?: [Dayjs, Dayjs] | null;
  includeDeleted: boolean;
}

function toQuery(filter: FilterState, current: number, pageSize: number): AiAdminConversationQuery {
  return {
    current,
    pageSize,
    keyword: filter.keyword || undefined,
    userId: filter.userId || undefined,
    startAt: filter.range?.[0] ? filter.range[0].format('YYYY-MM-DDTHH:mm:ss') : undefined,
    endAt: filter.range?.[1] ? filter.range[1].format('YYYY-MM-DDTHH:mm:ss') : undefined,
    includeDeleted: filter.includeDeleted,
  };
}

/**
 * 全员 AI 对话记录（仅超级管理员可见）。
 * 服务端同样只认 superadmin，这里的判断只是提前给出友好提示。
 */
const AiConversationRecords = () => {
  const { id: companyId, currentUser } = useCompany();
  const isSuperAdmin = Boolean(currentUser?.isSuperAdmin);

  const [filter, setFilter] = useState<FilterState>({ includeDeleted: true });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [active, setActive] = useState<AiAdminConversation | null>(null);

  const query = toQuery(filter, pagination.current, pagination.pageSize);

  const { data: page, loading, refresh: refreshList } = useRequest(
    () => aiAssistantAdminApi.listConversations(companyId, query),
    { ready: isSuperAdmin, refreshDeps: [companyId, JSON.stringify(query)] },
  );

  const { data: summary, loading: summaryLoading, refresh: refreshSummary } = useRequest(
    () => aiAssistantAdminApi.summary(companyId, query),
    { ready: isSuperAdmin, refreshDeps: [companyId, JSON.stringify(query)] },
  );

  const refreshAll = () => {
    refreshList();
    refreshSummary();
  };

  const handleDelete = async (conversationId: string) => {
    await aiAssistantAdminApi.deleteConversation(companyId, conversationId);
    message.success('已删除');
    refreshAll();
  };

  const { data: messages, loading: messagesLoading } = useRequest(
    async () => (active ? foldMessages(await aiAssistantAdminApi.listMessages(companyId, active.id)) : []),
    { ready: Boolean(active), refreshDeps: [companyId, active?.id] },
  );

  if (!isSuperAdmin) {
    return <Result status="403" title="仅超级管理员可查看全员对话记录" />;
  }

  const columns: ColumnsType<AiAdminConversation> = [
    {
      title: '会话',
      dataIndex: 'title',
      render: (title: string, row) => (
        <Space size={4}>
          <Typography.Text>{title}</Typography.Text>
          {row.deleted && <Tag color="default">已删除</Tag>}
        </Space>
      ),
    },
    {
      title: '发起人',
      dataIndex: 'userName',
      width: 150,
      render: (userName: string | null, row) =>
        userName ? (
          // 点姓名即按该用户筛选，省掉一个员工选择器
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => {
              setFilter((prev) => ({ ...prev, userId: String(row.userId) }));
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
          >
            {userName}
          </Button>
        ) : (
          EMPTY_PLACEHOLDER
        ),
    },
    { title: '部门', dataIndex: 'departmentName', width: 130, render: (v) => v || EMPTY_PLACEHOLDER },
    { title: '消息数', dataIndex: 'messageCount', width: 90, render: (v) => formatTokens(v) },
    {
      // 四个用量指标挤成一列：input / output / total / cache，表格横向空间有限
      title: 'tokens（输入/输出/合计/缓存）',
      dataIndex: 'totalTokens',
      width: 220,
      render: (_, row) => (
        <Tooltip
          title={
            <div>
              <div>输入：{formatExactTokens(row.promptTokens)}</div>
              <div>输出：{formatExactTokens(row.completionTokens)}</div>
              <div>合计：{formatExactTokens(row.totalTokens)}</div>
              <div>命中缓存：{formatExactTokens(row.cachedTokens)}</div>
            </div>
          }
        >
          <span>
            {formatTokens(row.promptTokens)} / {formatTokens(row.completionTokens)} /{' '}
            {formatTokens(row.totalTokens)} / {formatCacheHitRate(row.cachedTokens, row.promptTokens)}
          </span>
        </Tooltip>
      ),
    },
    { title: '总耗时', dataIndex: 'durationMs', width: 110, render: (v) => formatDuration(v) },
    {
      title: '模型',
      dataIndex: 'models',
      width: 180,
      render: (models: string[] | undefined) =>
        models && models.length > 0
          ? models.map((model) => <Tag key={model}>{model}</Tag>)
          : EMPTY_PLACEHOLDER,
    },
    {
      title: '最近活跃',
      dataIndex: 'lastMessageAt',
      width: 160,
      render: (value: string | null) => (value ? dayjs(value).format(DATE_FORMAT) : EMPTY_PLACEHOLDER),
    },
    {
      title: '操作',
      width: 150,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          <Button type="link" style={{ padding: 0 }} onClick={() => setActive(row)}>
            查看对话
          </Button>
          {/* 唯一的删除入口：普通用户在 AI 助手页已经没有删除按钮 */}
          <Popconfirm
            title="删除该对话？"
            description="软删除，消息与用量记录保留，仍可在本页查到。"
            okText="删除"
            cancelText="取消"
            disabled={Boolean(row.deleted)}
            onConfirm={() => handleDelete(row.id)}
          >
            <Button type="link" danger style={{ padding: 0 }} disabled={Boolean(row.deleted)}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="AI 对话记录"
        subTitle="全公司所有人的 AI 助手对话与 token 用量（仅超级管理员可见）"
        extra={[
          <Button key="refresh" icon={<ReloadOutlined />} onClick={refreshAll} loading={loading}>
            刷新
          </Button>,
        ]}
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline">
          <Form.Item label="关键字">
            <Input.Search
              allowClear
              placeholder="会话标题或发起人姓名"
              style={{ width: 240 }}
              onSearch={(value) => {
                setFilter((prev) => ({ ...prev, keyword: value }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </Form.Item>
          <Form.Item label="活跃时间">
            <DatePicker.RangePicker
              showTime
              style={{ width: 340 }}
              onChange={(range) => {
                setFilter((prev) => ({ ...prev, range: range as [Dayjs, Dayjs] | null }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </Form.Item>
          <Form.Item label="含已删除">
            <Switch
              checked={filter.includeDeleted}
              onChange={(checked) => {
                setFilter((prev) => ({ ...prev, includeDeleted: checked }));
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
            />
          </Form.Item>
          {filter.userId && (
            <Form.Item>
              <Tag closable onClose={() => setFilter((prev) => ({ ...prev, userId: undefined }))}>
                仅看用户 {filter.userId}
              </Tag>
            </Form.Item>
          )}
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }} loading={summaryLoading}>
        <Row gutter={16}>
          <Col span={3}>
            <Statistic title="会话数" value={summary?.conversationCount ?? 0} />
          </Col>
          <Col span={3}>
            <Statistic title="使用人数" value={summary?.userCount ?? 0} />
          </Col>
          <Col span={3}>
            <Statistic title="消息数" value={summary?.messageCount ?? 0} />
          </Col>
          <Col span={4}>
            <Statistic title="tokens 合计" value={formatTokens(summary?.totalTokens)} />
          </Col>
          <Col span={3}>
            <Statistic title="输入 tokens" value={formatTokens(summary?.promptTokens)} />
          </Col>
          <Col span={3}>
            <Statistic title="输出 tokens" value={formatTokens(summary?.completionTokens)} />
          </Col>
          <Col span={3}>
            <Statistic
              title="缓存利用率"
              value={formatCacheHitRate(summary?.cachedTokens, summary?.promptTokens)}
            />
          </Col>
          <Col span={2}>
            <Statistic title="总耗时" value={formatDuration(summary?.durationMs)} />
          </Col>
        </Row>
        <div style={{ marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            使用模型：
          </Typography.Text>
          {summary?.models && summary.models.length > 0
            ? summary.models.map((model) => <Tag key={model}>{model}</Tag>)
            : EMPTY_PLACEHOLDER}
        </div>
      </Card>

      <Table
        rowKey="id"
        size="small"
        scroll={{ x: 1600 }}
        loading={loading}
        columns={columns}
        dataSource={page?.list || []}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: page?.total || 0,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
        }}
      />

      <Drawer
        width={860}
        open={Boolean(active)}
        onClose={() => setActive(null)}
        title={active?.title}
        destroyOnClose
      >
        {active && (
          <Space direction="vertical" size={4} style={{ marginBottom: 12 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {active.userName || EMPTY_PLACEHOLDER}
              {active.departmentName ? ` · ${active.departmentName}` : ''}
              {` · 输入 ${formatTokens(active.promptTokens)} / 输出 ${formatTokens(active.completionTokens)}`}
              {` · 缓存 ${formatCacheHitRate(active.cachedTokens, active.promptTokens)}`}
              {` · 耗时 ${formatDuration(active.durationMs)}`}
            </Typography.Text>
            <div>{(active.models || []).map((model) => <Tag key={model}>{model}</Tag>)}</div>
          </Space>
        )}
        <Spin spinning={messagesLoading}>
          {messages && messages.length > 0 ? (
            messages.map((message, index) => (
              <MessageBubble key={message.id ?? `admin-${index}`} message={message} />
            ))
          ) : (
            <Empty description="没有消息" />
          )}
        </Spin>
      </Drawer>
    </div>
  );
};

export default AiConversationRecords;
